import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ArrowRight, WarningCircle } from 'phosphor-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';

function PaymentSuccess() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  
  
  // Get URL parameters
  const orderId = searchParams.get('order_id');
  const orderNumber = searchParams.get('order_number');
  const paymentIntentId = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');
  const amountParam = searchParams.get('amount');
  const currency = searchParams.get('currency') || 'EUR';
  
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('processing');

  // Clear cart when payment is successfully verified
  useEffect(() => {
    if (paymentStatus === 'succeeded' && orderDetails) {
      clearCart();
    }
  }, [paymentStatus, orderDetails, clearCart]);

  const verifyPayment = useCallback(async () => {
    try {
      console.log('=== Payment Success Page Debug ===');
      console.log('URL Parameters:', {
        orderId,
        orderNumber,
        paymentIntentId,
        redirectStatus,
        amountParam,
        currency
      });

      // Check redirect status from Stripe
      if (redirectStatus) {
        console.log('Stripe redirect status:', redirectStatus);
        if (redirectStatus === 'succeeded') {
          setPaymentStatus('succeeded');
        } else if (redirectStatus === 'failed') {
          setPaymentStatus('failed');
          setError('Er was een probleem bij het verwerken van je betaling');
          setLoading(false);
          return;
        }
      }

      // Try to get order data from localStorage (primary source)
      let orderData = null;
      
      // Look for order by order_number (most reliable)
      if (orderNumber) {
        const orderByNumber = localStorage.getItem(`order_${orderNumber}`);
        if (orderByNumber) {
          orderData = JSON.parse(orderByNumber);
          console.log('Found order by number in localStorage:', orderData);
        }
      }
      
      // Fallback: Look for order by order_id
      if (!orderData && orderId) {
        const orderById = localStorage.getItem(`order_${orderId}`);
        if (orderById) {
          orderData = JSON.parse(orderById);
          console.log('Found order by ID in localStorage:', orderData);
        }
      }

      // If we found order data, display it
      if (orderData) {
        // Update with payment intent if provided and not already set
        if (paymentIntentId && !orderData.payment_id.includes(paymentIntentId)) {
          orderData.payment_id = paymentIntentId;
          // Update localStorage with payment intent
          localStorage.setItem(`order_${orderData.id}`, JSON.stringify(orderData));
          localStorage.setItem(`order_${orderData.order_number}`, JSON.stringify(orderData));
        }
        
        // Orders are now created automatically via webhook when payment succeeds
        // No need to create order here in the frontend
        
        setOrderDetails(orderData);
        setPaymentStatus('succeeded');
        console.log('Order details loaded successfully');
      }
      // Fallback: Create minimal order from URL parameters if no localStorage data
      else if (orderNumber && amountParam) {
        console.log('Creating minimal order from URL parameters');
        const parsedAmount = parseFloat(amountParam);
        const minimalOrder = {
          id: `order_${Date.now()}`,
          order_number: orderNumber,
          payment_id: paymentIntentId,
          total_amount: parsedAmount,
          currency: currency,
          status: 'completed',
          payment_status: 'completed',
          items: [],
          created_at: new Date().toISOString()
        };
        
        // Store this minimal order for future reference
        localStorage.setItem(`order_${minimalOrder.id}`, JSON.stringify(minimalOrder));
        localStorage.setItem(`order_${minimalOrder.order_number}`, JSON.stringify(minimalOrder));
        
        // Orders are now created automatically via webhook when payment succeeds
        // No need to create order here in the frontend
        
        setOrderDetails(minimalOrder);
        setPaymentStatus('succeeded');
      }
      // No order data found anywhere
      else {
        setError('Bestelling informatie niet gevonden. Controleer je e-mail voor bevestiging of neem contact op met support.');
        setPaymentStatus('unknown');
      }

    } catch (err) {
      console.error('Error verifying payment:', err);
      setError('Kon betaling status niet verifiëren. Neem contact op met support als je hulp nodig hebt.');
      setPaymentStatus('error');
    } finally {
      setLoading(false);
    }
  }, [orderId, orderNumber, paymentIntentId, redirectStatus, amountParam, currency]);

  useEffect(() => {
    verifyPayment();
  }, [verifyPayment]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-ambre-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">{t('verifyingPayment', 'Betaling status controleren...')}</p>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {paymentStatus === 'succeeded' && orderDetails ? (
            <>
              {/* Success Header */}
              <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-8 text-center">
                <CheckCircle size={64} className="mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-2">Betaling succesvol!</h1>
                <p className="text-green-100">Bedankt voor je aankoop</p>
              </div>

              {/* Order Details */}
              <div className="p-8">
                <div className="space-y-6">
                  <div className="border-b pb-4">
                    <h2 className="text-xl font-semibold mb-2">Bestelling details</h2>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Bestelnummer:</span>
                        <p className="font-medium">{orderDetails.order_number || orderDetails.id}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Betaling ID:</span>
                        <p className="font-medium">{orderDetails.payment_id}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Betaald bedrag:</span>
                        <p className="font-medium">€{orderDetails.total_amount.toFixed(2)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <p className="font-medium text-green-600 capitalize">{orderDetails.status}</p>
                      </div>
                    </div>
                  </div>

                  {orderDetails.items && orderDetails.items.length > 0 ? (
                    <div>
                      <h3 className="font-semibold mb-3">Bestelde items</h3>
                      <div className="space-y-2">
                        {orderDetails.items.map((item, index) => (
                          <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                            <span>{item.name} × {item.quantity}</span>
                            <span className="font-medium">€{(item.price * item.quantity).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex justify-between items-center py-3 mt-4 border-t-2 border-ambre-200 font-semibold text-lg">
                        <span>Totaal</span>
                        <span className="text-ambre-600">€{orderDetails.total_amount.toFixed(2)}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-semibold text-blue-800 mb-2">✅ Betaling bevestigd</h3>
                      <p className="text-blue-700 text-sm">
                        Je betaling van <strong>€{orderDetails.total_amount.toFixed(2)}</strong> is succesvol verwerkt.
                      </p>
                    </div>
                  )}

                  <div className="bg-ambre-50 rounded-lg p-4">
                    <h3 className="font-semibold text-ambre-800 mb-2">Wat gebeurt er nu?</h3>
                    <p className="text-ambre-700 text-sm">
                      Je bestelling is bevestigd en wordt verwerkt. Je ontvangt binnenkort een e-mail bevestiging.
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => {
                        console.log('Navigate to shop clicked');
                        navigate('/shop');
                      }}
                      className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 transition-colors"
                    >
                      <span>Verder winkelen</span>
                      <ArrowRight size={20} />
                    </button>
                    
                    <button
                      onClick={() => {
                        console.log('Navigate to my-orders clicked');
                        navigate('/my-orders');
                      }}
                      className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                      Alle bestellingen bekijken
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : paymentStatus === 'failed' ? (
            <>
              {/* Failure Header */}
              <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-8 text-center">
                <WarningCircle size={64} className="mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-2">Betaling mislukt</h1>
                <p className="text-red-100">Je betaling kon niet worden verwerkt</p>
              </div>

              <div className="p-8 text-center">
                <p className="text-gray-600 mb-6">
                  Helaas was je betaling niet succesvol. Probeer het opnieuw of gebruik een andere betaalmethode.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate('/cart')}
                    className="px-6 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 transition-colors"
                  >
                    Opnieuw proberen
                  </button>
                  <button
                    onClick={() => navigate('/contact')}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Contact opnemen
                  </button>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Error/Unknown State */}
              <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-8 text-center">
                <WarningCircle size={64} className="mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-2">Betaling status onbekend</h1>
                <p className="text-yellow-100">We hebben moeite met het verifiëren van je betaling</p>
              </div>

              <div className="p-8 text-center">
                <p className="text-gray-600 mb-4">{error || 'Kan betaling status niet verifiëren.'}</p>
                <p className="text-sm text-gray-500 mb-6">
                  Als je denkt dat dit een fout is, controleer dan je e-mail voor een bevestiging of neem contact op met ons support team.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate('/my-orders')}
                    className="px-6 py-3 bg-ambre-600 text-white rounded-lg hover:bg-ambre-700 transition-colors"
                  >
                    Mijn bestellingen controleren
                  </button>
                  <button
                    onClick={() => navigate('/contact')}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Contact opnemen
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PaymentSuccess; 